import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FormFieldComponent from '@/components/FormFieldComponent';
import Loader from '@/components/Loader';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useGetWorkOrderById } from '@/services/workOrderService';
import {
  getProcessBillingServer,
  patchProcessBillingServer,
  postProcessBillingServer,
} from '@/services/processBillingServerService';
import { uploadMedia } from '@/services/uploadMedia';
import {
  getAssignedServersForProcessBilling,
  isProcessBillingServerEligible,
} from '@/pages/caseManagement/processBilling/processBillingServerUtils';
import { isProcessBillingVendorEligible } from '@/pages/caseManagement/processBilling/processBillingVendorUtils';
import type { ProcessBillingServerGetResponse } from '@/types/processBillingServer.types';
import type { WorkOrderGetByIdResponse } from '@/types/workOrder.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload, User, X } from 'lucide-react';
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUserStore } from '@/stores/user';
import { canAccessProcessBilling, isAdminOrSupervisor as isAdminOrSupervisorUser } from '@/services/userService';
import { toast } from '@/components/ui/use-toast';
import { Feature } from '@/utils/constant';
import { hasFeature } from '@/services/featureService';
import { formatCurrency } from '@/lib/utils';

const MAX_PDF_BYTES = 20 * 1024 * 1024;

const PROOF_OF_SERVICE_HELPER = 'Proof of Service is required. Upload one or more PDF files (max 20 MB each).';

function watchMoney(raw: unknown): number {
  if (raw === null || raw === undefined || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return 0;
    const n = Number(t);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export type DocFormRow = {
  clientKey: string;
  name: string;
  mediaId: string;
  processBillingDocumentId?: string;
};

export type EntryFormRow = {
  serverId: string;
  serverCode?: string | null;
  serverName?: string;
  expense: string | null;
  income: string | null;
  documents: DocFormRow[];
};

export type ProcessBillingFormValues = {
  entries: EntryFormRow[];
};

function parseMoney(label: string, raw: string | null | undefined): number {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) {
    throw new Error(`${label} is required`);
  }
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error(`${label} must be a number ≥ 0`);
  }
  const rounded = Math.round(n * 100) / 100;
  if (Math.abs(rounded - n) > 1e-6) {
    throw new Error(`${label} may have at most 2 decimal places`);
  }
  return rounded;
}

function buildCreateDefaults(wo: WorkOrderGetByIdResponse): ProcessBillingFormValues {
  const servers = getAssignedServersForProcessBilling(wo);
  return {
    entries: servers.map((s) => ({
      serverId: s.serverId,
      serverCode: s.serverCode,
      serverName: s.displayName,
      expense: '',
      income: '',
      documents: [{ clientKey: crypto.randomUUID(), name: '', mediaId: '', processBillingDocumentId: undefined }],
    })),
  };
}

function buildEditDefaults(
  wo: WorkOrderGetByIdResponse,
  billing: ProcessBillingServerGetResponse
): ProcessBillingFormValues {
  const servers = getAssignedServersForProcessBilling(wo);
  return {
    entries: servers.map((s) => {
      const row = billing.billingEntries.find((e) => e.serverId === s.serverId);
      if (!row) {
        return {
          serverId: s.serverId,
          serverCode: s.serverCode,
          serverName: s.displayName,
          expense: '',
          income: '',
          documents: [{ clientKey: crypto.randomUUID(), name: '', mediaId: '' }],
        };
      }
      return {
        serverId: s.serverId,
        serverCode: row.serverCode ?? s.serverCode,
        serverName: s.displayName,
        expense: String(row.expense),
        income: String(row.income),
        documents: row.proofOfService.map((d) => ({
          clientKey: crypto.randomUUID(),
          name: d.name,
          mediaId: d.mediaId,
          processBillingDocumentId: d.processBillingDocumentId,
        })),
      };
    }),
  };
}

function affidavitNameForSlot(index: number): string {
  return index === 0 ? 'Affidavit' : `Affidavit ${index}`;
}

function EntryDocumentsSection({
  entryIndex,
  filesRef,
}: {
  entryIndex: number;
  filesRef: MutableRefObject<Record<string, File | undefined>>;
}) {
  const { control, register, watch, getValues, setValue } = useFormContext<ProcessBillingFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `entries.${entryIndex}.documents`,
  });

  // Backfill clientKey for any field missing one so submit and onChange agree on the
  // file storage key. The hidden input alone is racy with reset().
  useEffect(() => {
    fields.forEach((_field, docIdx) => {
      const current = getValues(`entries.${entryIndex}.documents.${docIdx}.clientKey`);
      if (typeof current !== 'string' || current.length === 0) {
        setValue(`entries.${entryIndex}.documents.${docIdx}.clientKey`, crypto.randomUUID(), {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
  }, [fields, entryIndex, getValues, setValue]);

  const docClientKey = (docIdx: number): string => {
    const fromForm = getValues(`entries.${entryIndex}.documents.${docIdx}.clientKey`);
    if (typeof fromForm === 'string' && fromForm.length > 0) return fromForm;
    const w = watch(`entries.${entryIndex}.documents.${docIdx}.clientKey`);
    if (typeof w === 'string' && w.length > 0) return w;
    const fallback = fields[docIdx]?.id ?? '';
    if (fallback) {
      // Persist into form state so submit reads the same key.
      setValue(`entries.${entryIndex}.documents.${docIdx}.clientKey`, fallback, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
    return fallback;
  };

  const [, forceRerender] = useState(0);

  return (
    <div className="mt-8 rounded-lg bg-[#F5F5F5] p-5">
      <div className="flex flex-row justify-between items-start mb-4">
        <div>
          <div className="text-base font-semibold text-primary">Proof of Service</div>
          <p className="text-sm text-placeholder italic">{PROOF_OF_SERVICE_HELPER}</p>
        </div>
        <button
          type="button"
          aria-label="Add document"
          className="bg-white border border-bordercolor rounded-md p-1.5 text-primary hover:bg-[#EDF5FF]"
          onClick={() =>
            append({ clientKey: crypto.randomUUID(), name: '', mediaId: '', processBillingDocumentId: undefined })
          }
        >
          <Plus className="h-4 w-4 text-primary" />
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {fields.map((field, docIdx) => {
          const mediaId = watch(`entries.${entryIndex}.documents.${docIdx}.mediaId`);
          const docName = watch(`entries.${entryIndex}.documents.${docIdx}.name`);
          const ck = docClientKey(docIdx);
          const existingDocLabel = `${docName?.trim() || affidavitNameForSlot(docIdx)}.pdf`;
          const fileLabel = ck && filesRef.current[ck] ? filesRef.current[ck].name : mediaId ? existingDocLabel : '';
          const inputId = `pof-${entryIndex}-${docIdx}`;
          return (
            <div key={field.id} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <input type="hidden" {...register(`entries.${entryIndex}.documents.${docIdx}.clientKey`)} />
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Document Name</Label>
                <Input
                  className="bg-white"
                  placeholder={affidavitNameForSlot(docIdx)}
                  {...register(`entries.${entryIndex}.documents.${docIdx}.name`)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium invisible select-none">spacer</span>
                <label
                  htmlFor={inputId}
                  className="flex flex-row items-center gap-2 rounded-md bg-white border border-dashed border-[#92A1B3] px-4 h-12 cursor-pointer"
                >
                  <Upload className="h-5 w-5 text-primary shrink-0" strokeWidth={2} />
                  <span
                    className={`text-sm flex-1 truncate ${fileLabel ? 'text-primary' : 'text-placeholder'}`}
                    title={fileLabel || 'Click to upload a file'}
                  >
                    {fileLabel || 'Click to upload a file'}
                  </span>
                  {fileLabel ? (
                    <button
                      type="button"
                      aria-label="Remove file"
                      className="text-primary hover:text-[#12375C]"
                      onClick={(e) => {
                        e.preventDefault();
                        const k = docClientKey(docIdx);
                        if (k) delete filesRef.current[k];
                        const el = document.getElementById(inputId) as HTMLInputElement | null;
                        if (el) el.value = '';
                        if (fields.length > 1) {
                          remove(docIdx);
                        } else {
                          // Clear the mediaId on the single remaining row so the user can re-pick.
                          setValue(`entries.${entryIndex}.documents.${docIdx}.mediaId`, '', {
                            shouldDirty: true,
                          });
                          forceRerender((n) => n + 1);
                        }
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </label>
                <input
                  id={inputId}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    const k = docClientKey(docIdx);
                    if (!k) {
                      e.target.value = '';
                      return;
                    }
                    if (!file) {
                      delete filesRef.current[k];
                      e.target.value = '';
                      forceRerender((n) => n + 1);
                      return;
                    }
                    if (file.type !== 'application/pdf') {
                      delete filesRef.current[k];
                      toast({ description: 'Proof of service must be a PDF.', variant: 'destructive' });
                      e.target.value = '';
                      forceRerender((n) => n + 1);
                      return;
                    }
                    if (file.size > MAX_PDF_BYTES) {
                      delete filesRef.current[k];
                      toast({ description: 'PDF must be 20 MB or smaller.', variant: 'destructive' });
                      e.target.value = '';
                      forceRerender((n) => n + 1);
                      return;
                    }
                    filesRef.current[k] = file;
                    forceRerender((n) => n + 1);
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const ProcessBillingServerFormPage = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname.endsWith('/edit');
  const user = useUserStore((s) => s.user);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('0');
  const filesRef = useRef<Record<string, File | undefined>>({});
  const initializedKeyRef = useRef<string | null>(null);

  const woQuery = useGetWorkOrderById(workOrderId);
  const billingQuery = useQuery({
    queryKey: ['processBillingServer', workOrderId],
    queryFn: () => getProcessBillingServer(workOrderId!),
    enabled: Boolean(workOrderId),
    retry: false,
  });

  const form = useForm<ProcessBillingFormValues>({
    defaultValues: { entries: [] },
  });

  const flagOn = hasFeature(Feature.PROCESS_BILLING_SERVER) || woQuery.data?.service?.type === 'PROCESS_SERVICE';
  if (!woQuery.isFetching && woQuery.data && !flagOn) {
    navigate('/404', { replace: true });
    return null;
  }

  const { handleSubmit, reset, control, watch, setError } = form;

  useEffect(() => {
    if (!woQuery.data || !isProcessBillingServerEligible(woQuery.data)) return;
    const existingBatchId = billingQuery.data?.processBillingBatchId ?? null;
    const initKey = `${workOrderId}::${isEdit ? 'edit' : 'create'}`;

    if (!isEdit) {
      // Create flow: if a batch already exists, send the user to the View page (avoids 409).
      if (existingBatchId) {
        navigate(`/process/workorder/${workOrderId}/process-billing`, { replace: true });
        return;
      }
      if (initializedKeyRef.current === initKey) return;
      initializedKeyRef.current = initKey;
      filesRef.current = {};
      reset(buildCreateDefaults(woQuery.data));
      return;
    }
    // Edit flow: redirect to /create when no batch exists.
    if (billingQuery.isError) {
      navigate(`/process/workorder/${workOrderId}/process-billing/create`, { replace: true });
      return;
    }
    if (!billingQuery.data) return;
    if (!existingBatchId) {
      navigate(`/process/workorder/${workOrderId}/process-billing/create`, { replace: true });
      return;
    }
    if (initializedKeyRef.current === initKey) return;
    initializedKeyRef.current = initKey;
    filesRef.current = {};
    reset(buildEditDefaults(woQuery.data, billingQuery.data));
  }, [woQuery.data, billingQuery.data, billingQuery.isError, isEdit, reset, navigate, workOrderId]);

  const createMutation = useMutation({
    mutationFn: async (body: Parameters<typeof postProcessBillingServer>[1]) => {
      if (!workOrderId) throw new Error('Missing work order');
      return postProcessBillingServer(workOrderId, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['processBillingServer', workOrderId] });
      toast({ description: 'Process billing saved.' });
      navigate(`/process/workorder/${workOrderId}/process-billing`);
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
          : undefined;
      toast({ description: msg ?? 'Save failed.', variant: 'destructive' });
    },
  });

  const patchMutation = useMutation({
    mutationFn: async (body: Parameters<typeof patchProcessBillingServer>[1]) => {
      if (!workOrderId) throw new Error('Missing work order');
      return patchProcessBillingServer(workOrderId, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['processBillingServer', workOrderId] });
      toast({ description: 'Process billing updated.' });
      navigate(`/process/workorder/${workOrderId}/process-billing`);
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
          : undefined;
      toast({ description: msg ?? 'Update failed.', variant: 'destructive' });
    },
  });

  const isAdminOrSupervisor = isAdminOrSupervisorUser(user);
  const canSubmit =
    isAdminOrSupervisor ||
    (canAccessProcessBilling(user) && !isEdit);
  const billingBlocked = isEdit && !isAdminOrSupervisor;

  const onSubmit = async (values: ProcessBillingFormValues) => {
    if (billingBlocked) return;
    try {
      const billingEntries = [];
      for (const [entryIndex, ent] of values.entries.entries()) {
        let expense: number;
        let income: number;
        try {
          expense = parseMoney('Expense', ent.expense);
        } catch (e) {
          setError(`entries.${entryIndex}.expense`, {
            type: 'manual',
            message: e instanceof Error ? e.message : 'Invalid expense',
          });
          setTab(String(entryIndex));
          return;
        }
        try {
          income = parseMoney('Income', ent.income);
        } catch (e) {
          setError(`entries.${entryIndex}.income`, {
            type: 'manual',
            message: e instanceof Error ? e.message : 'Invalid income',
          });
          setTab(String(entryIndex));
          return;
        }
        const proof: Record<string, unknown>[] = [];

        if (!ent.documents?.length) {
          toast({ description: 'Proof of Service is required.', variant: 'destructive' });
          return;
        }

        for (const [di, d] of ent.documents.entries()) {
          let mediaId = d.mediaId?.trim() ?? '';
          const file = filesRef.current[d.clientKey];
          if (!mediaId && file) {
            try {
              const uploaded = await uploadMedia(file, 'PROCESS_BILLING');
              if (!uploaded?.mediaId) {
                toast({ description: 'Failed to upload PDF. Please try again.', variant: 'destructive' });
                return;
              }
              mediaId = uploaded.mediaId;
            } catch (error) {
              toast({
                description: `Failed to upload PDF. Please try again.${error instanceof Error ? ' ' + error.message : ''}`,
                variant: 'destructive',
              });
              return;
            }
          }
          if (!mediaId) {
            toast({ description: 'Proof of Service is required.', variant: 'destructive' });
            return;
          }
          const rawName = d.name?.trim() ?? '';
          const name = rawName || affidavitNameForSlot(di);
          if (isEdit && d.processBillingDocumentId) {
            proof.push({
              processBillingDocumentId: d.processBillingDocumentId,
              mediaId,
              name,
              sortOrder: di,
            });
          } else if (isEdit) {
            proof.push({ mediaId, name, sortOrder: di });
          } else {
            proof.push({ mediaId, name });
          }
        }

        billingEntries.push({
          serverId: ent.serverId,
          expense,
          income,
          proofOfService: proof,
        });
      }

      if (isEdit) {
        patchMutation.mutate({
          billingEntries: billingEntries as never,
        });
      } else {
        createMutation.mutate({
          billingEntries: billingEntries as never,
        });
      }
    } catch (e) {
      toast({
        description: e instanceof Error ? e.message : 'Validation error',
        variant: 'destructive',
      });
    }
  };

  const wo = woQuery.data;
  const loading = (woQuery.isFetching && !woQuery.data) || (billingQuery.isFetching && !billingQuery.data);

  if (billingBlocked) {
    return (
      <div className="w-full max-w-xl rounded-lg border border-destructive/40 p-8 text-center space-y-4">
        <h1 className="text-lg font-semibold text-destructive">Permission denied</h1>
        <p className="text-sm text-placeholder">
          Your role cannot edit process billing. Supervisors and administrators may make changes.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(`/process/workorder/${workOrderId}/process-billing`)}
        >
          Back to view
        </Button>
      </div>
    );
  }

  const entries = watch('entries') ?? [];
  const woNumber = wo?.number ?? '';
  const titleSuffix = isEdit ? 'Edit Billing Entry' : 'Add Billing Entry';

  return (
    <Loader isLoading={loading}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full h-full min-w-[170px] flex flex-col gap-6">
          <h1 className="text-2xl font-semibold text-primary tracking-tight">{`W/O: ${woNumber} -  ${titleSuffix}`}</h1>

          {!wo || !isProcessBillingServerEligible(wo) ? (
            isProcessBillingVendorEligible(wo) ? (
              <div className="rounded-lg border border-bordercolor bg-[#F5F5F5] p-6 space-y-4 max-w-xl">
                <p className="text-sm text-primary">
                  This work order uses vendor process billing. The server billing entry form does not apply when no
                  server is assigned for billing—use vendor billing instead.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    className="h-10"
                    onClick={() => navigate(`/process/workorder/${workOrderId}/process-billing-vendor/create`)}
                  >
                    Add Vendor Billing
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 border-primary text-primary"
                    onClick={() => navigate(`/process/workorder/${workOrderId}/process-billing`)}
                  >
                    Back to billing summary
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-destructive">
                This work order is not eligible for server process billing.
              </div>
            )
          ) : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-[#F5F5F5] h-12 w-full justify-start rounded-none p-0 border-b border-bordercolor flex overflow-x-auto scrollbar-hide">
                {entries.map((ent, idx) => (
                  <TabsTrigger
                    key={ent.serverId}
                    value={String(idx)}
                    className="h-12 px-3 gap-2 rounded-none border-r border-bordercolor shrink-0 text-sm font-medium text-primary data-[state=active]:bg-[#EDF5FF] data-[state=inactive]:bg-[#E2E2E2] data-[state=active]:shadow-none"
                  >
                    <User className="h-5 w-5" />
                    <span>{ent.serverName ?? `Server ${idx + 1}`}</span>
                  </TabsTrigger>
                ))}
                <button
                  type="button"
                  aria-label="Add server (managed via work order assignment)"
                  disabled
                  className="h-12 w-12 shrink-0 bg-primary text-white flex items-center justify-center cursor-not-allowed"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </TabsList>
              {entries.map((ent, idx) => {
                const incomeNum = watchMoney(watch(`entries.${idx}.income`));
                const expenseNum = watchMoney(watch(`entries.${idx}.expense`));
                const total = incomeNum + expenseNum;
                return (
                  <TabsContent key={ent.serverId} value={String(idx)} className="mt-6 space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      <div className="flex flex-col gap-1">
                        <Label className="text-sm font-medium">Server ID</Label>
                        <Input
                          value={ent.serverCode ?? '—'}
                          readOnly
                          disabled
                          className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-sm font-medium">Server&apos;s Name</Label>
                        <Input
                          value={ent.serverName ?? ''}
                          readOnly
                          disabled
                          className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                        />
                      </div>
                      <FormFieldComponent
                        name={`entries.${idx}.income`}
                        control={control}
                        label="Income"
                        placeholder="Enter Income"
                        type="number"
                        required
                        allowNegativeNumbers={false}
                        maxDecimalPlaces={2}
                        step="0.01"
                      />
                      <FormFieldComponent
                        name={`entries.${idx}.expense`}
                        control={control}
                        label="Expense"
                        placeholder="Enter Expense"
                        type="number"
                        required
                        allowNegativeNumbers={false}
                        maxDecimalPlaces={2}
                        step="0.01"
                      />
                      <div className="flex flex-col gap-1">
                        <Label className="text-sm font-medium">Total</Label>
                        <Input
                          value={formatCurrency(total)}
                          readOnly
                          disabled
                          className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                        />
                      </div>
                      <div aria-hidden className="hidden md:block" />
                    </div>
                    <EntryDocumentsSection entryIndex={idx} filesRef={filesRef} />
                  </TabsContent>
                );
              })}
            </Tabs>
          )}

          <div className="flex flex-row gap-3 justify-end mt-6">
            <Button
              type="button"
              variant="outline"
              className="w-[125px] h-[48px] border-primary text-primary hover:bg-[#EDF5FF]"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Button
              type="submit"
              className="w-[125px] h-[48px] hover:bg-[#12375C]"
              disabled={
                !canSubmit ||
                createMutation.isPending ||
                patchMutation.isPending ||
                !wo ||
                !isProcessBillingServerEligible(wo)
              }
            >
              Save
            </Button>
          </div>
        </form>
      </FormProvider>
    </Loader>
  );
};
